<template>
  <BaseModal title="Table Columns" :closeModal="close" size="modal_500p">
    <div>
      <p class="color_grey mb_15 font_14p">
        Tick a column to show it, drag to change its position. This layout is
        shared with every admin.
      </p>

      <draggable
        class="column_settings_list"
        tag="ul"
        v-model="localColumns"
        item-key="field"
        handle=".column_settings_handle"
        :animation="200"
        :force-fallback="true"
        ghost-class="column_settings_ghost"
        chosen-class="column_settings_chosen"
        drag-class="column_settings_drag"
      >
        <template #item="{ element }">
          <li class="column_settings_row d_flex_start">
            <span class="column_settings_handle" title="Drag to reorder">
              <svg width="12" height="18" viewBox="0 0 12 18" fill="currentColor">
                <circle cx="3" cy="3" r="1.5" />
                <circle cx="9" cy="3" r="1.5" />
                <circle cx="3" cy="9" r="1.5" />
                <circle cx="9" cy="9" r="1.5" />
                <circle cx="3" cy="15" r="1.5" />
                <circle cx="9" cy="15" r="1.5" />
              </svg>
            </span>
            <div class="form_checkbox">
              <input
                type="checkbox"
                :id="'column_' + element.field"
                v-model="element.visible"
              />
              <label :for="'column_' + element.field">{{ element.label }}</label>
            </div>
          </li>
        </template>
      </draggable>

      <p v-if="noneVisible" class="color_danger mt_10 font_14p">
        Select at least one column.
      </p>
    </div>

    <div class="bottom_btn">
      <button
        type="button"
        class="btn btn_transparent has_border_radius mr_auto"
        @click="resetToDefault"
      >
        Reset to default
      </button>
      <button
        type="button"
        class="btn btn_transparent has_border_radius"
        @click="close"
      >
        Cancel
      </button>
      <button
        type="button"
        class="btn btn_black has_border_radius ml_10 width_100p"
        :disabled="saving || noneVisible"
        @click="save"
      >
        Save
      </button>
    </div>
  </BaseModal>
</template>

<script>
import draggable from 'vuedraggable';
import BaseModal from '@/components/BaseModal';

export default {
  name: 'TicketColumnSettingsModal',
  components: { BaseModal, draggable },
  emits: ['save', 'close'],
  props: {
    // Configurable columns only, in their current order: [{field, label, visible}].
    columns: {
      type: Array,
      required: true,
    },
    // Same shape, the built-in order/visibility, for "Reset to default".
    defaultColumns: {
      type: Array,
      required: true,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Edited in place; the parent only sees it on Save, so Cancel discards.
      localColumns: this.copy(this.columns),
    };
  },
  computed: {
    noneVisible() {
      return !this.localColumns.some((column) => column.visible);
    },
  },
  methods: {
    copy(columns) {
      return columns.map((column) => ({ ...column }));
    },
    resetToDefault() {
      this.localColumns = this.copy(this.defaultColumns);
    },
    save() {
      if (this.noneVisible) return;

      this.$emit('save', this.copy(this.localColumns));
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.column_settings_list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.column_settings_row {
  padding: 6px 8px;
  border: 1px solid #e5e5e5;
  border-radius: 3px;
  margin-bottom: 6px;
  background: #fff;
}

.column_settings_handle {
  display: flex;
  align-items: center;
  cursor: grab;
  color: #999;
  margin-right: 10px;
}

.column_settings_handle:active {
  cursor: grabbing;
}

/* The gap the row will drop into. */
.column_settings_ghost {
  opacity: 0.4;
  background: #f2f4f7;
  border-style: dashed;
}

/* The row under the cursor while it is held. */
.column_settings_chosen {
  border-color: #b9bec7;
}

/* forceFallback clones the row onto <body>; this styles that floating copy. */
.column_settings_drag {
  opacity: 1 !important;
  background: #fff;
  border-color: #b9bec7;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
  transform: scale(1.01);
}

/* .bottom_btn is only defined inside .right_menu, so a modal footer needs its own rules. */
.bottom_btn {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-top: 1px solid #e8e9eb;
  padding-top: 15px;
  margin-top: 15px;
}

.mr_auto {
  margin-right: auto;
}
</style>
